import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import TripMap from '../components/booking/TripMap';
import AppStoreLink from '../components/common/AppStoreLink';
import { useTripTracking } from '../hooks/useTripTracking';
import { formatEta, formatRoute, statusToHuman, tripHeadline } from '../utils/tripFormatting';

/**
 * Live tracking for one trip.
 *
 * Read-only, like the rest of the account area. Actions that change a trip —
 * cancelling, messaging the driver, approving a fare change — stay in the app,
 * so this page never offers a control it cannot honour.
 *
 * The connection state is shown rather than hidden. A customer watching a map
 * that has quietly stopped updating has no way to tell "the driver has not
 * moved" from "we lost the connection", and the second is our problem to
 * disclose, not theirs to guess.
 */
const TripDetailPage: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const { trip, driverPosition, driverHeading, liveEtaSeconds, routePolyline, connected, loading, missing } =
    useTripTracking(tripId);

  const route = formatRoute(trip?.pickupAddress, trip?.dropoffAddress);
  const eta = formatEta(liveEtaSeconds ?? trip?.etaSeconds);

  const pickup = useMemo(
    () =>
      trip?.pickupLatitude != null && trip?.pickupLongitude != null
        ? { lat: trip.pickupLatitude, lng: trip.pickupLongitude }
        : null,
    [trip?.pickupLatitude, trip?.pickupLongitude],
  );
  const dropoff = useMemo(
    () =>
      trip?.dropoffLatitude != null && trip?.dropoffLongitude != null
        ? { lat: trip.dropoffLatitude, lng: trip.dropoffLongitude }
        : null,
    [trip?.dropoffLatitude, trip?.dropoffLongitude],
  );

  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section">
          <div className="container">
            <div className="sw-section-head">
              <span className="sw-eyebrow">Live trip</span>
              <h1>{trip ? tripHeadline(trip.phase, trip.status) : 'Your trip'}</h1>
              {route ? <p>{route}</p> : null}
            </div>

            {loading ? <p className="sw-trip-note">Loading this trip…</p> : null}

            {missing && !loading ? (
              <div>
                <p className="sw-trip-note">
                  This trip is no longer in progress. Completed trips are listed in your deliveries.
                </p>
                <div className="sw-hero-actions sw-trip-actions">
                  <Link className="sw-btn-secondary" to="/account">
                    Back to your deliveries
                  </Link>
                  <AppStoreLink className="sw-link-action" placement="trip_detail_missing">
                    Open the app
                  </AppStoreLink>
                </div>
              </div>
            ) : null}

            {trip ? (
              <>
                <TripMap
                  driver={driverPosition}
                  driverHeading={driverHeading}
                  dropoff={dropoff}
                  label={`Map of the trip from ${trip.pickupAddress ?? 'pickup'} to ${
                    trip.dropoffAddress ?? 'drop-off'
                  }`}
                  pickup={pickup}
                  routePolyline={routePolyline}
                />

                <dl className="sw-trip-facts">
                  <div>
                    <dt>Status</dt>
                    <dd>{statusToHuman(trip.status)}</dd>
                  </div>
                  {eta ? (
                    <div>
                      <dt>Arriving in</dt>
                      <dd>{eta}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Booking</dt>
                    <dd>{trip.bookingId}</dd>
                  </div>
                </dl>

                {/* Silence is ambiguous; say which kind it is. */}
                <p className="sw-trip-note">
                  {connected
                    ? 'Updating live.'
                    : 'Live updates are unavailable — refreshing every 20 seconds instead.'}
                </p>

                {trip.stale ? (
                  <p className="sw-trip-alert">
                    Trip needs support{trip.staleReason ? `: ${trip.staleReason}` : ''}
                  </p>
                ) : null}

                <div className="sw-hero-actions sw-trip-actions">
                  <Link className="sw-btn-secondary" to="/account">
                    All deliveries
                  </Link>
                  {/* This page answers "where is it now". The record answers
                      "what is this delivery" — the reference, the fare, the
                      load notes, the attachments — and a customer watching a
                      map should not have to go back to the list to reach it. */}
                  <Link
                    className="sw-link-action"
                    to={`/account/trips/${encodeURIComponent(tripId ?? '')}/details`}
                  >
                    Full details &rarr;
                  </Link>
                  {/* Messaging, cancelling and fare approval live in the app. */}
                  <AppStoreLink className="sw-link-action" placement="trip_detail">
                    Message or cancel in the app
                  </AppStoreLink>
                </div>
              </>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TripDetailPage;
